import _ from "lodash";
import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react";
import { DateKeys, FiltersStateType, FoldersType, Keys, KeysList, RestKeys } from "src/pages/DashboardPage/acadia/type";
import { CheckBoxListItem, DateListItem, UserListItem } from "src/types/main";
import { getLocalItem, setLocalItem } from "src/utils/localStorage";

interface FiltersContextType {
  filters: FiltersStateType;
  addFilter: (key: KeysList, value: CheckBoxListItem | UserListItem | DateListItem) => void;
  removeFilter: (key: KeysList, value: CheckBoxListItem | UserListItem | DateListItem) => void;
  resetFilter: (key: KeysList) => void;
  updateFilter: (filter: FiltersStateType) => void;
  resetFilters: () => void;
  fromFolderToFilters: (folders: FoldersType) => void;
  statusFilters: boolean;
}

const FiltersContext = createContext<FiltersContextType | null>(null);

export const useFiltersContext = (): FiltersContextType => {
  const context = useContext(FiltersContext);

  if (!context) {
    throw new Error("useFiltersContext должен использоваться внутри FiltersProvider");
  }

  return context;
};

interface FiltersProviderProps {
  initial: FiltersStateType;
  children: ReactNode;
}

export const FiltersProvider: React.FC<FiltersProviderProps> = ({ initial, children }) => {
  const localFilters: FiltersStateType | null = getLocalItem("filters_object");

  const [filters, setFilters] = useState(localFilters ?? initial);
  const [statusFilters, setStatusFilters] = useState<boolean>(false);

  function addFilter<Key extends KeysList>(key: Key, value: CheckBoxListItem | UserListItem | DateListItem | any) {
    let result = _.cloneDeep(filters);

    if (result[key]) {
      let newFilters = { ...result };

      if (key === "dateForRevision" && _.isObject(result[key])) {
        newFilters = { ...newFilters, [key as DateKeys]: value as DateListItem };
      } else if (_.isArray(result[key as Keys | RestKeys]) && value.title) {
        newFilters = { ...newFilters, [key]: [...result[key as Keys | RestKeys], { ...value, key }] };
      }

      setFilters(newFilters);
    }
  }

  function resetFilter(key: KeysList) {
    let result = _.cloneDeep(filters);
    let newFilters = { ...result };

    if (key === "dateForRevision") {
      newFilters = {
        ...newFilters,
        [key]: {
          id: 0,
          title: "",
          date: { start: null, end: null },
          required: false,
        },
      };
    } else if (result[key]) {
      newFilters = { ...newFilters, [key]: [] };
    }
    setFilters(newFilters);
  }

  const isFiltersEmpty = useCallback((data: FiltersStateType) => {
    const orderKeys: Keys[] = ["collections", "departments", "sectors", "processes", "types"];

    const baseFilters = _.fromPairs(_.map(orderKeys, (key) => [key, data[key]]));

    let foundEmpty = false;

    const result = orderKeys.every((key) => {
      const items = baseFilters[key];
      if (items.length === 0) {
        foundEmpty = true;
        return true;
      } else {
        if (foundEmpty) {
          return false;
        }
      }
      // Если дошли до сюда, значит массив не пустой и до этого пустых не было
      return true;
    });

    return !result || _.values(baseFilters).some((f) => f.length > 1);
  }, []);

  function resetFilters() {
    setFilters(initial);
  }

  function removeFilter(key: KeysList, value: CheckBoxListItem | UserListItem | DateListItem) {
    let result = _.cloneDeep(filters);

    if (result[key]) {
      let newFilters = { ...result };
      if (key === "dateForRevision") {
        newFilters = {
          ...newFilters,
          [key]: value as DateListItem,
        };
      } else {
        newFilters = {
          ...newFilters,
          [key]: result[key].filter((item) => item.id !== value.id),
        };
      }

      setFilters(newFilters);
    }
  }

  function updateFilter(data: FiltersStateType) {
    setFilters(data);
  }

  function fromFolderToFilters(folders: FoldersType) {
    const localFilters: FiltersStateType | null = getLocalItem("filters_object");

    const initialFilters = Object.keys(folders).reduce((acc, currentKey) => {
      const folderItem = folders[currentKey as keyof typeof folders];

      if (folderItem !== null) {
        acc[currentKey as Keys] = [folderItem];
      } else {
        acc[currentKey as Keys] = [];
      }

      return acc;
    }, _.cloneDeep(localFilters) as FiltersStateType);

    setFilters(initialFilters);
  }

  useEffect(() => {
    setLocalItem("filters_object", filters);
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    const obj = isFiltersEmpty(filters);

    setStatusFilters(obj);
  }, [JSON.stringify(filters), isFiltersEmpty]);

  const contextValue: FiltersContextType = {
    filters,
    statusFilters: statusFilters,
    addFilter,
    updateFilter,
    removeFilter,
    resetFilter,
    resetFilters,
    fromFolderToFilters,
  };

  return <FiltersContext.Provider value={contextValue}>{children}</FiltersContext.Provider>;
};
